/**
 * Personal Data Search Utilities
 *
 * Purpose: Provides search functionality across encrypted personal data
 *
 * Features:
 * - Search across categories, entries, and field data
 * - Support for encrypted field search
 * - Category and field type filtering
 * - Fuzzy matching for better user experience
 * - Performance optimization with caching
 */

import React from 'react';
import { decryptValue } from './encryption';
import { searchCache, SearchAnalytics } from './search-cache';
import type { CategoryEntry } from '@xanectech/types';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - search');

export interface SearchFilters {
  categories?: string[]; // Category IDs to filter by
  fieldTypes?: string[]; // Field types to filter by (email, phone, etc.)
  completionStatus?: 'complete' | 'incomplete' | 'all';
  mandatory?: boolean | 'all';
  recent?: number; // Days for recent filter
  dataTypes?: ('pii' | 'sensitive' | 'public')[]; // Data sensitivity levels
  hasEntries?: boolean | 'all'; // Whether category has entries
  entryTypes?: ('primary' | 'favorite' | 'regular')[]; // Entry types
  sortBy?: 'relevance' | 'recent' | 'alphabetical' | 'completion';
  sortOrder?: 'asc' | 'desc';
}

export interface SearchResult {
  type: 'category' | 'entry' | 'field';
  id: string;
  categoryId: string;
  categoryName: string;
  entryId?: string;
  entryLabel?: string;
  fieldId?: string;
  fieldName?: string;
  fieldType?: string;
  matchText: string;
  decryptedValue?: string;
  relevance: number; // 0-1 score
}

interface SearchFieldDefinition {
  name?: string;
  field_type?: string;
  is_pii?: boolean;
  is_sensitive?: boolean;
}

interface SearchCategory {
  id: string;
  name: string;
  description?: string;
  is_mandatory?: boolean;
  contains_pii?: boolean;
  contains_sensitive?: boolean;
  field_definitions?: SearchFieldDefinition[];
}

interface SearchFieldData {
  id: string | number;
  entry_id: string | number;
  value_encrypted?: string | null;
  field_definition?: SearchFieldDefinition | null;
}

const isSearchResultArray = (value: unknown): value is SearchResult[] =>
  Array.isArray(value);

/**
 * Search across personal data categories, entries, and fields
 */
export class PersonalDataSearchService {
  private static normalizeText(text: string): string {
    return text.toLowerCase().trim();
  }

  private static calculateRelevance(
    searchTerm: string,
    text: string,
    exactMatch: boolean = false,
  ): number {
    const normalizedTerm = this.normalizeText(searchTerm);
    const normalizedText = this.normalizeText(text);

    if (normalizedText === normalizedTerm) return 1.0;
    if (exactMatch && normalizedText.includes(normalizedTerm)) return 0.8;

    // Fuzzy matching
    const words = normalizedTerm.split(' ');
    let matchCount = 0;

    for (const word of words) {
      if (normalizedText.includes(word)) {
        matchCount++;
      }
    }

    return words.length > 0 ? matchCount / words.length : 0;
  }

  /**
   * Search categories by name and description
   */
  static searchCategories(
    categories: SearchCategory[],
    searchTerm: string,
    filters: SearchFilters = {},
  ): SearchResult[] {
    if (!searchTerm.trim()) return [];

    const results: SearchResult[] = [];

    for (const category of categories) {
      // Apply category filters
      if (
        filters.categories?.length &&
        !filters.categories.includes(category.id)
      ) {
        continue;
      }

      if (filters.mandatory !== 'all' && filters.mandatory !== undefined) {
        if (filters.mandatory !== category.is_mandatory) {
          continue;
        }
      }

      // Apply data type filters (check if category has fields matching data sensitivity)
      if (filters.dataTypes?.length) {
        const categoryHasMatchingDataTypes = filters.dataTypes.some(
          (dataType) => {
            switch (dataType) {
              case 'pii':
                return (
                  category.contains_pii ||
                  category.field_definitions?.some(
                    (fieldDef) => fieldDef.is_pii,
                  )
                );
              case 'sensitive':
                return (
                  category.contains_sensitive ||
                  category.field_definitions?.some(
                    (fieldDef) => fieldDef.is_sensitive,
                  )
                );
              case 'public':
                return !category.contains_pii && !category.contains_sensitive;
              default:
                return false;
            }
          },
        );

        if (!categoryHasMatchingDataTypes) {
          continue;
        }
      }

      // Search in category name
      const nameRelevance = this.calculateRelevance(
        searchTerm,
        category.name,
        true,
      );
      if (nameRelevance > 0.3) {
        results.push({
          type: 'category',
          id: category.id,
          categoryId: category.id,
          categoryName: category.name,
          matchText: category.name,
          relevance: nameRelevance * 0.9, // Slightly lower than exact matches
        });
      }

      // Search in category description
      if (category.description) {
        const descRelevance = this.calculateRelevance(
          searchTerm,
          category.description,
        );
        if (descRelevance > 0.4) {
          results.push({
            type: 'category',
            id: `${category.id}_desc`,
            categoryId: category.id,
            categoryName: category.name,
            matchText: category.description,
            relevance: descRelevance * 0.7, // Lower relevance for description matches
          });
        }
      }
    }

    return results;
  }

  /**
   * Search entries by label and metadata
   */
  static searchEntries(
    entries: CategoryEntry[],
    categories: SearchCategory[],
    searchTerm: string,
    filters: SearchFilters = {},
  ): SearchResult[] {
    if (!searchTerm.trim()) return [];

    const results: SearchResult[] = [];
    const categoryMap = new Map(categories.map((cat) => [cat.id, cat]));

    for (const entry of entries) {
      const category = categoryMap.get(entry.category_id);
      if (!category) continue;

      // Apply filters
      if (
        filters.categories?.length &&
        !filters.categories.includes(String(entry.category_id))
      ) {
        continue;
      }

      if (filters.recent) {
        const createdAt = entry.created_at
          ? new Date(String(entry.created_at)).getTime()
          : Date.now();
        const daysDiff = (Date.now() - createdAt) / (1000 * 60 * 60 * 24);
        if (daysDiff > filters.recent) {
          continue;
        }
      }

      // Apply entry type filters
      if (filters.entryTypes?.length) {
        const entryMatchesTypes = filters.entryTypes.some((entryType) => {
          switch (entryType) {
            case 'primary':
              return entry.is_primary;
            case 'favorite':
              return entry.is_favorite;
            case 'regular':
              return !entry.is_primary && !entry.is_favorite;
            default:
              return false;
          }
        });

        if (!entryMatchesTypes) {
          continue;
        }
      }

      // Search in entry label
      const labelRelevance = this.calculateRelevance(
        searchTerm,
        String(entry.entry_label),
        true,
      );
      if (labelRelevance > 0.3) {
        results.push({
          type: 'entry',
          id: entry.id,
          categoryId: entry.category_id,
          categoryName: category.name,
          entryId: entry.id,
          entryLabel: entry.entry_label,
          matchText: entry.entry_label,
          relevance: labelRelevance,
        });
      }

      // Note: Could add search in entry metadata/tags here if available in future
    }

    return results;
  }

  /**
   * Search encrypted field data
   * This is computationally expensive as it requires decryption
   */
  static async searchFieldData(
    fieldDataList: SearchFieldData[],
    categories: SearchCategory[],
    entries: CategoryEntry[],
    searchTerm: string,
    filters: SearchFilters = {},
  ): Promise<SearchResult[]> {
    if (!searchTerm.trim()) return [];

    const results: SearchResult[] = [];
    const categoryMap = new Map(categories.map((cat) => [cat.id, cat]));
    const entryMap = new Map(entries.map((entry) => [entry.id, entry]));

    // Process fields in batches to avoid overwhelming the encryption service
    const batchSize = 10;

    for (let i = 0; i < fieldDataList.length; i += batchSize) {
      const batch = fieldDataList.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (fieldData) => {
          const entry = entryMap.get(String(fieldData.entry_id));
          if (!entry) return;

          const category = categoryMap.get(entry.category_id);
          if (!category) return;

          // Apply filters
          if (
            filters.categories?.length &&
            !filters.categories.includes(String(entry.category_id))
          ) {
            return;
          }

          if (filters.fieldTypes?.length && fieldData.field_definition) {
            if (
              !filters.fieldTypes.includes(
                String(fieldData.field_definition.field_type),
              )
            ) {
              return;
            }
          }

          try {
            // Decrypt and search field value
            if (fieldData.value_encrypted) {
              const decryptedValue = await decryptValue(
                String(fieldData.value_encrypted),
              );

              if (decryptedValue) {
                const fieldRelevance = this.calculateRelevance(
                  searchTerm,
                  decryptedValue,
                  true,
                );

                if (fieldRelevance > 0.5) {
                  results.push({
                    type: 'field',
                    id: String(fieldData.id),
                    categoryId: entry.category_id,
                    categoryName: category.name,
                    entryId: entry.id,
                    entryLabel: entry.entry_label,
                    fieldId: String(fieldData.id),
                    fieldName: fieldData.field_definition?.name || 'Field',
                    fieldType: fieldData.field_definition?.field_type,
                    matchText: decryptedValue,
                    decryptedValue,
                    relevance: fieldRelevance,
                  });
                }
              }
            }

            // Also search field definition names
            if (fieldData.field_definition?.name) {
              const fieldNameRelevance = this.calculateRelevance(
                searchTerm,
                String(fieldData.field_definition.name),
              );

              if (fieldNameRelevance > 0.6) {
                results.push({
                  type: 'field',
                  id: `${String(fieldData.id)}_name`,
                  categoryId: entry.category_id,
                  categoryName: category.name,
                  entryId: entry.id,
                  entryLabel: entry.entry_label,
                  fieldId: String(fieldData.id),
                  fieldName: fieldData.field_definition.name,
                  fieldType: fieldData.field_definition.field_type,
                  matchText: fieldData.field_definition.name,
                  relevance: fieldNameRelevance * 0.9,
                });
              }
            }
          } catch (error) {
            logger.warn('Error searching field data:', {
              error: String(error),
            });
            // Skip fields that can't be decrypted
          }
        }),
      );
    }

    return results;
  }

  /**
   * Sort search results based on criteria
   */
  static sortSearchResults(
    results: SearchResult[],
    sortBy: string = 'relevance',
    sortOrder: 'asc' | 'desc' = 'desc',
  ): SearchResult[] {
    const sortedResults = [...results].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'relevance':
          comparison = a.relevance - b.relevance;
          break;
        case 'recent':
          // This would need entry/category creation dates - using type as fallback
          comparison = a.type.localeCompare(b.type);
          break;
        case 'alphabetical':
          comparison = (a.entryLabel || a.categoryName).localeCompare(
            b.entryLabel || b.categoryName,
          );
          break;
        case 'completion':
          // Categories with entries vs without - using type as proxy
          comparison = a.type.localeCompare(b.type);
          break;
        default:
          comparison = a.relevance - b.relevance;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return sortedResults;
  }

  /**
   * Comprehensive search across all data types
   */
  static async searchAll(
    categories: SearchCategory[],
    entries: CategoryEntry[],
    fieldDataList: SearchFieldData[],
    searchTerm: string,
    filters: SearchFilters = {},
  ): Promise<SearchResult[]> {
    if (!searchTerm.trim()) return [];

    // Check cache first
    const cachedResult = searchCache.get(searchTerm, filters);
    if (isSearchResultArray(cachedResult)) {
      return cachedResult;
    }

    // Run searches in parallel
    const [categoryResults, entryResults, fieldResults] = await Promise.all([
      Promise.resolve(this.searchCategories(categories, searchTerm, filters)),
      Promise.resolve(
        this.searchEntries(entries, categories, searchTerm, filters),
      ),
      this.searchFieldData(
        fieldDataList,
        categories,
        entries,
        searchTerm,
        filters,
      ),
    ]);

    // Combine and sort results
    const allResults = [...categoryResults, ...entryResults, ...fieldResults];

    // Apply sorting
    const sortedResults = this.sortSearchResults(
      allResults,
      filters.sortBy,
      filters.sortOrder,
    );

    const finalResults = sortedResults.slice(0, 50); // Limit results to prevent UI overload

    // Cache the results
    searchCache.set(searchTerm, filters, finalResults);

    // Track search analytics
    const analytics = SearchAnalytics.getInstance();
    analytics.trackSearch(searchTerm, finalResults.length, filters);

    return finalResults;
  }

  /**
   * Get search suggestions based on existing data
   */
  static getSearchSuggestions(
    categories: SearchCategory[],
    entries: CategoryEntry[],
    fieldDataList: SearchFieldData[],
  ): string[] {
    const suggestions = new Set<string>();

    // Add category names
    categories.forEach((cat) => {
      if (cat.name) suggestions.add(String(cat.name).toLowerCase());
    });

    // Add entry labels
    entries.forEach((entry) => {
      if (entry.entry_label)
        suggestions.add(String(entry.entry_label).toLowerCase());
    });

    // Add field definition names
    fieldDataList.forEach((field) => {
      if (field.field_definition?.name) {
        suggestions.add(String(field.field_definition.name).toLowerCase());
      }
    });

    // Add common field types
    ['email', 'phone', 'address', 'name', 'date', 'url'].forEach((type) => {
      suggestions.add(type);
    });

    return Array.from(suggestions).slice(0, 20);
  }
}

/**
 * Debounced search hook for real-time search
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
